import Vue from "vue";
import { tagService } from "../services/tag.service";

const state = {
	tags: [],
	hasFetchedAllTags: false,
	loading: false,
	contentTagAssociations: {},
};

const getters = {
	allTags: (state) => state.tags,
	tagsForContent: (state) => (contentID) => state.contentTagAssociations[contentID] || [],
};

function showError(message) {
	Vue.toasted.error(message || "Something went wrong", {
		icon: "exclamation-triangle",
		theme: "outline",
		position: "bottom-center",
		duration: 3000,
	});
}

const actions = {
	async fetchTags({ commit, state }) {
		if (state.hasFetchedAllTags) return state.tags;

		commit("setLoading", true);
		try {
			let tagsResponse = await tagService.fetchTags();
			commit("setTags", tagsResponse?.payload || []);
		} catch (error) {
			showError(error);
		} finally {
			commit("setLoading", false);
			commit("setHasFetchedAllTags", true);
		}
		return state.tags;
	},

	async createTag({ commit }, tagName) {
		try {
			let createTagResponse = await tagService.createTag(tagName);
			let newTag = { id: createTagResponse.payload, name: tagName };
			commit("addTag", newTag);
			return newTag;
		} catch (error) {
			showError(error);
			return null;
		}
	},

	async fetchChapterTagAssociations({ commit }, chapterID) {
		try {
			let chapterAssociationsResponse = await tagService.fetchChapterTagAssociations(chapterID);
			commit("setChapterTagAssociations", chapterAssociationsResponse?.payload || []);
			return chapterAssociationsResponse?.payload || [];
		} catch (error) {
			showError(error);
			return [];
		}
	},

	async createTagAssociation({ commit }, { tagID, entity, entityID, tagName }) {
		try {
			let createAssociationResponse = await tagService.createTagAssociation(tagID, entity, entityID);
			if (entity === "content" && createAssociationResponse?.payload) {
				commit("addContentTagAssociation", {
					contentID: entityID,
					tag: { id: tagID, name: tagName, associationID: createAssociationResponse.payload },
				});
			}
			return createAssociationResponse?.payload;
		} catch (error) {
			showError(error);
			return null;
		}
	},

	async deleteTagAssociation({ commit }, { associationID, contentID }) {
		try {
			let deleteAssociationResponse = await tagService.deleteTagAssociation(associationID);
			commit("removeContentTagAssociation", { contentID, associationID });
			return deleteAssociationResponse?.payload;
		} catch (error) {
			showError(error);
			return null;
		}
	},
};

const mutations = {
	setTags(state, tags) {
		state.tags = tags || [];
	},
	setHasFetchedAllTags(state, hasFetched) {
		state.hasFetchedAllTags = hasFetched;
	},
	addTag(state, tag) {
		state.tags.push(tag);
	},
	setChapterTagAssociations(state, associations) {
		let tagsGroupedByContentId = {};
		(associations || []).forEach((association) => {
			let contentId = association.entityID;
			if (!tagsGroupedByContentId[contentId]) tagsGroupedByContentId[contentId] = [];
			tagsGroupedByContentId[contentId].push({
				id: association.tagID,
				name: association.tagName,
				associationID: association.tagAssociationID,
			});
		});
		state.contentTagAssociations = { ...state.contentTagAssociations, ...tagsGroupedByContentId };
	},
	addContentTagAssociation(state, { contentID, tag }) {
		let existingTagsForContent = state.contentTagAssociations[contentID] || [];
		state.contentTagAssociations = {
			...state.contentTagAssociations,
			[contentID]: [...existingTagsForContent, tag],
		};
	},
	removeContentTagAssociation(state, { contentID, associationID }) {
		let existingTagsForContent = state.contentTagAssociations[contentID] || [];
		state.contentTagAssociations = {
			...state.contentTagAssociations,
			[contentID]: existingTagsForContent.filter((tag) => tag.associationID !== associationID),
		};
	},
	setLoading(state, loading) {
		state.loading = loading;
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
